import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { stageAPI } from '../services/api';
import { Etiquette, Ajout, Bandeaux, ADRESSE_RE } from './StageFenetreEnvoi';
import { useTailleFenetre } from './fenetreTaille';
import './StageFenetreEnvoi.css';

/**
 * SUIVI DE STAGE — LA FENÊTRE D'ENVOI À LA PROMOTION (lot 3.S-c)
 * ===============================================================
 * Spec : doc/specs_techniques/courriel_smtp.md §5 (envoi collectif), §5 bis SM1 / SM6
 * Maquette : `#certifEnvoi` en mode collectif (doc/maquettes/suivi_de_stage_maquette.html)
 *
 * SM1 — un message PERSONNEL par étudiant. La fenêtre a deux onglets :
 *   · « Rédiger » : UN texte pour tous, où le prénom et le nom sont des ÉTIQUETTES BLEUES
 *     protégées (impossible de figer « Bonjour Yanis » pour les 26) ;
 *   · « Aperçu » : le message EXACT de chacun, en lecture seule, ◀ 1/N ▶.
 * Une ligne par étudiant : décocher le retire, l'adresse se corrige dans la ligne.
 * Les personnes en copie reçoivent UN récapitulatif. L'envoi tourne en tâche de fond : la
 * fenêtre suit la progression, puis montre le bilan, message par message.
 */
const copier = async (texte) => {
    try { if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(texte); return true; } } catch (_) { /* repli */ }
    try {
        const z = document.createElement('textarea'); z.value = texte; z.style.position = 'fixed'; z.style.opacity = '0';
        document.body.appendChild(z); z.select(); const ok = document.execCommand('copy'); document.body.removeChild(z); return ok;
    } catch (_) { return false; }
};

/**
 * Le message d'UNE ligne, rendu à l'écran à partir du texte de « Rédiger » (aperçu).
 * Lot 3.5b-4 : une ligne peut porter ses propres valeurs (`vars` : prénom, nom, nombre…) et son propre
 * BLOC (`bloc_html` : la liste des étudiants d'un tuteur). Sans elles : le prénom et le nom de l'étudiant.
 */
const valeurDe = (etu, v) => (etu.vars && etu.vars[v] !== undefined ? etu.vars[v] : (v === 'nom' ? (etu.nom || '') : (etu.prenom || '')));
function rendreMessage(html, etu) {
    const cadre = document.createElement('div');
    cadre.innerHTML = (html || '').replace(/\u200b/g, '');   // lot 3.5d-0 : le caractère qui tient le curseur après une étiquette
    cadre.querySelectorAll('.envoi-var').forEach((v) => {
        v.replaceWith(document.createTextNode(valeurDe(etu, v.getAttribute('data-var'))));
    });
    cadre.querySelectorAll('.envoi-bloc').forEach((b) => {
        const z = document.createElement('div'); z.innerHTML = etu.bloc_html || ''; b.replaceWith(...z.childNodes);
    });
    cadre.querySelectorAll('.envoi-lien').forEach((l) => {
        const a = document.createElement('a'); a.className = 'fe-lien-rendu'; a.textContent = l.textContent.trim(); l.replaceWith(a);
    });
    return cadre.innerHTML;
}

/**
 * Lot PA.3 (Planning annuel, maquette « envoi 2 » validée le 28/10) — LES GROUPES, facultatifs : quand le brouillon porte
 * `groupes` ([{ cle, libelle, externe }]) et que chaque ligne porte son `groupe`, la liste se range en groupes REPLIÉS
 * (leur compte : « 11 / 12 cochés · 1 sans adresse ») ; un clic déplie les personnes (une case chacune, l'adresse
 * corrigeable), la case du groupe coche tout / rien (partielle : état intermédiaire) ; une recherche par nom déplie les
 * groupes qui le contiennent. Sans `groupes`, la fenêtre garde sa liste simple — les autres modules ne changent pas.
 */
function ListeGroupes({ groupes, lignes, setLignes }) {
    const [ouverts, setOuverts] = useState(() => new Set());
    const [filtre, setFiltre] = useState('');
    const f = filtre.trim().toLowerCase();
    const trouve = (l) => !f || [l.nom, l.prenom, l.a_libelle, l.info].some((x) => String(x || '').toLowerCase().includes(f));
    const basculer = (cle) => setOuverts((o) => { const s = new Set(o); if (s.has(cle)) s.delete(cle); else s.add(cle); return s; });
    return (
        <div className="fe-groupes">
            {lignes.length > 12 && (
                <div className="fe-groupes-recherche">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input className="input-sm" value={filtre} onChange={(e) => setFiltre(e.target.value)} placeholder="Chercher un nom…" aria-label="Chercher un nom" />
                    <span className="fe-aide" style={{ margin: 0 }}>cliquer un groupe le déplie</span>
                </div>
            )}
            {groupes.map((g) => {
                const lesLignes = lignes.map((l, i) => ({ l, i })).filter((x) => x.l.groupe === g.cle);
                if (!lesLignes.length) return null;
                const visibles = lesLignes.filter((x) => trouve(x.l));
                if (f && !visibles.length) return null;
                const avecAdresse = lesLignes.filter((x) => x.l.adresse);
                const cochees = lesLignes.filter((x) => x.l.coche).length;
                const sans = lesLignes.filter((x) => !x.l.origine).length;
                const tout = cochees > 0 && cochees === avecAdresse.length;
                const ouvert = ouverts.has(g.cle) || !!f;
                const cocherGroupe = (v) => setLignes((ls) => ls.map((l) => (l.groupe === g.cle ? { ...l, coche: v && !!l.adresse } : l)));
                return (
                    <div key={g.cle} className={'fe-groupe' + (g.externe ? ' externe' : '') + (ouvert ? ' ouvert' : '')}>
                        <div className="fe-groupe-h" onClick={() => basculer(g.cle)}>
                            <i className="fa-solid fa-chevron-right fe-groupe-chev" />
                            <input type="checkbox" checked={tout} aria-label={`Tout le groupe ${g.libelle}`}
                                   ref={(el) => { if (el) el.indeterminate = cochees > 0 && !tout; }}
                                   onClick={(e) => e.stopPropagation()} onChange={(e) => cocherGroupe(e.target.checked)} />
                            <b>{g.libelle}</b>{g.externe && <span className="fe-aide" style={{ margin: 0 }}>· extérieurs</span>}
                            <span className="fe-groupe-compte"><b>{cochees}</b> / {lesLignes.length} coché{cochees > 1 ? 's' : ''}
                                {sans > 0 && <span className="fe-ko"> · {sans} sans adresse</span>}</span>
                        </div>
                        {ouvert && (
                            <div className="fe-groupe-corps">
                                {visibles.map(({ l, i }) => {
                                    const corrigee = l.adresse !== l.origine && !!l.adresse;
                                    return (
                                        <div key={l.dossier_id} className={'fe-groupe-l' + (l.coche ? '' : ' fe-decochee')}>
                                            <input type="checkbox" checked={!!l.coche} aria-label={`Envoyer à ${l.a_libelle || `${l.nom} ${l.prenom}`}`}
                                                   onChange={(e) => setLignes((ls) => ls.map((x, k) => (k === i ? { ...x, coche: e.target.checked } : x)))} />
                                            <span>{l.a_libelle || `${l.nom} ${l.prenom}`}
                                                {l.info && <span className="fe-groupe-info">{l.info}</span>}
                                                {l.note && <span className="fe-tag fe-tag-alerte">{l.note}</span>}</span>
                                            <input className={'input-sm fe-adr-input' + (corrigee ? ' fe-adr-corrigee' : '') + (l.coche && !ADRESSE_RE.test(String(l.adresse || '').trim()) ? ' fe-refus' : '')}
                                                   value={l.adresse} placeholder={l.origine ? '' : 'aucune adresse — saisissez-en une'}
                                                   onChange={(e) => setLignes((ls) => ls.map((x, k) => (k === i ? { ...x, adresse: e.target.value, coche: x.coche || (!x.origine && !!e.target.value) } : x)))} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * `api` (lot C.4a) : la Certification fournit `lancer(departementId, { edition, essai })` et `etat(departementId, id)`
 * — même fenêtre (C32). Le brouillon peut porter `titre`, `apercu_chiffre` (à lancer, maîtres trouvés, sans
 * certificateur), `note_figement` et une `note` par ligne ; `mailto_secours: false` retire la messagerie (C31).
 */
function StageFenetrePromotion({ brouillon, departementId, onFermer, onTermine, api = null, onModeles = null }) {
    const taille = useTailleFenetre();          // 2026-10-10 : la même taille que les autres fenêtres d'envoi
    const [lignes, setLignes] = useState(() => (brouillon.etudiants || []).map((e) => ({ ...e, origine: e.adresse, prochains: false })));
    const [cc, setCc] = useState(() => (brouillon.cc || []).map((c) => ({ ...c })));
    const [cci, setCci] = useState([]);
    /*
     * Lot PA.3 — PLUSIEURS TEXTES, facultatifs : `brouillon.textes` = { genre: { libelle, icone, type_mail, sujet, html,
     * aide, jetons_objet } } et chaque ligne porte son `genre` (le Planning annuel : étudiants, maîtres). Un onglet
     * « Rédiger » par texte ; `sujet` et le texte affiché sont ceux du texte ACTIF, les autres attendent dans `textesRef`.
     * Sans `textes`, un seul texte, comme avant.
     */
    const multi = !!brouillon.textes;
    const genres = multi ? Object.keys(brouillon.textes) : [];
    const [genreActif, setGenreActif] = useState(genres[0] || null);
    const textesRef = useRef(multi ? Object.fromEntries(genres.map((g) => [g, { sujet: brouillon.textes[g].sujet, html: brouillon.textes[g].html }])) : null);
    // « Retouché » par onglet : « Modèles de courriel… » ne remplace sans demander qu'un texte que personne n'a touché.
    // ⚠️ Un REPÈRE posé à la frappe, pas une comparaison au texte d'origine : le navigateur réécrit le HTML de la zone
    // (guillemets, entités), le texte affiché ne serait jamais « identique » à celui reçu (vu au banc, 2026-10-28).
    const retoucheRef = useRef({});
    const [versionTexte, setVersionTexte] = useState(0);     // remonte la zone de texte quand son contenu est remplacé
    const [remplacement, setRemplacement] = useState(null);  // { genre, sujet, html } — le modèle a changé, texte retouché
    const [sujet, setSujet] = useState(multi ? brouillon.textes[genres[0]].sujet : (brouillon.sujet || ''));
    const [onglet, setOnglet] = useState('rediger');
    const [index, setIndex] = useState(0);
    const [annuaire, setAnnuaire] = useState([]);
    const [occupe, setOccupe] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [retour, setRetour] = useState(null);
    const [garde, setGarde] = useState(null);           // { adresse, n } — même adresse
    const [menuEssai, setMenuEssai] = useState(false);
    const [adresseEssai, setAdresseEssai] = useState(brouillon.cci_auto || '');
    const [tache, setTache] = useState(null);           // la tâche suivie (envoi ou essai)
    const [fait, setFait] = useState(false);
    const texte = useRef(null);
    const htmlRef = useRef(brouillon.html || '');
    const poserTexte = useCallback((el) => { texte.current = el; if (el && el.innerHTML !== htmlRef.current) el.innerHTML = htmlRef.current; }, []);
    /**
     * Lot 3.5d-0 — insérer une ÉTIQUETTE au curseur (texte libre de l'Annuaire). ⚠️ Pas `execCommand('insertHTML')` :
     * vu au banc, en fin de paragraphe le navigateur posait l'étiquette APRÈS le paragraphe (à la ligne), et deux
     * insertions de suite arrivaient en ordre inverse ; il supprimait aussi l'espace tapée juste avant. Insertion
     * directe par une plage : dans le paragraphe, l'espace gardée (insécable), le curseur juste après l'étiquette.
     */
    const insererEtiquette = (x) => {
        const zone = texte.current;
        if (!zone) return;
        // ⚠️ Vu au banc : chaque insertion laisse des textes VIDES ; le « caractère avant le curseur » était alors vide et
        // la 2ᵉ étiquette se collait à la 1ʳᵉ (« TestALPHADEUX »). On fusionne d'abord (la sélection suit).
        zone.normalize();
        const sel = document.getSelection();
        let r;
        if (sel && sel.rangeCount && zone.contains(sel.getRangeAt(0).startContainer)) r = sel.getRangeAt(0);
        else {
            zone.focus();
            r = document.createRange();
            r.selectNodeContents(zone.lastElementChild && /^(P|DIV|LI)$/.test(zone.lastElementChild.tagName) ? zone.lastElementChild : zone);
            r.collapse(false);
        }
        r.deleteContents();
        // Le caractère juste avant le curseur : une espace finale devient insécable ; collé à un mot, on en ajoute une.
        let n = r.startContainer, k = r.startOffset;
        if (n.nodeType === 1 && k > 0 && n.childNodes[k - 1]?.nodeType === 3) { n = n.childNodes[k - 1]; k = n.textContent.length; r.setStart(n, k); r.collapse(true); }
        let prefixe = false;
        if (n.nodeType === 3 && k > 0) {
            const avant = n.textContent.charAt(k - 1);
            if (avant === ' ') { n.textContent = n.textContent.slice(0, k - 1) + '\u00a0' + n.textContent.slice(k); r.setStart(n, k); r.collapse(true); }
            // Coll\u00e9 \u00e0 un mot, ou juste apr\u00e8s une autre \u00e9tiquette (son caract\u00e8re de largeur nulle) : une espace ins\u00e9cable.
            else if (avant !== '\u00a0') prefixe = true;
        } else if (n.nodeType === 1 && k > 0 && n.childNodes[k - 1]?.classList?.contains('envoi-var')) prefixe = true;
        const etiquette = document.createElement('span');
        etiquette.className = 'envoi-var'; etiquette.setAttribute('data-var', x.var); etiquette.setAttribute('contenteditable', 'false');
        etiquette.textContent = x.libelle;
        // Après l'étiquette : un caractère de LARGEUR NULLE — il tient le curseur (une étiquette non modifiable n'en offre pas)
        // sans ajouter d'espace visible (« Alpha , » vu au banc). Le serveur et l'aperçu le retirent.
        const apres = document.createTextNode('\u200b');
        r.insertNode(apres); r.insertNode(etiquette);
        if (prefixe) r.insertNode(document.createTextNode('\u00a0'));
        const fin = document.createRange(); fin.setStartAfter(apres); fin.collapse(true);
        sel.removeAllRanges(); sel.addRange(fin);
        htmlRef.current = zone.innerHTML;
        retoucheRef.current[multi ? genreActif : '_'] = true;
    };
    const htmlCourant = () => (texte.current ? (htmlRef.current = texte.current.innerHTML) : htmlRef.current);
    const cleTexte = multi ? genreActif : '_';
    /** Le texte d'un genre : celui de l'onglet actif est lu à l'écran, les autres attendent dans `textesRef`. */
    const texteDe = (g) => (!multi || g === genreActif ? { sujet, html: htmlCourant() } : textesRef.current[g]);
    const changerTexte = (g) => {
        if (g === genreActif) return;
        textesRef.current[genreActif] = { sujet, html: htmlCourant() };
        htmlRef.current = textesRef.current[g].html;
        setSujet(textesRef.current[g].sujet);
        setGenreActif(g);
    };
    /** Pose un texte (venu du modèle) dans l'onglet `g`. */
    const poserTexteModele = (g, t) => {
        retoucheRef.current[g] = false;
        if (multi && g !== genreActif) { textesRef.current[g] = { sujet: t.sujet, html: t.html }; return; }
        htmlRef.current = t.html; setSujet(t.sujet); setVersionTexte((v) => v + 1);
    };
    /**
     * « Modèles de courriel… » (Bertrand, 28/10 : à généraliser à toutes les fenêtres d'envoi) : l'éditeur du modèle de
     * CET envoi s'ouvre ; enregistré, le texte de la fenêtre suit — sauf si vous l'aviez retouché : on demande.
     */
    const ouvrirModeles = async () => {
        const g = cleTexte;
        const typeMail = multi ? brouillon.textes[g].type_mail : brouillon.type_mail;
        const t = await onModeles(typeMail);
        if (!t) return;
        if (retoucheRef.current[g]) setRemplacement({ genre: g, ...t }); else poserTexteModele(g, t);
    };

    useEffect(() => {
        stageAPI.annuaireEnvoi(departementId).then((r) => setAnnuaire(r.data.personnes || [])).catch(() => setAnnuaire([]));
    }, [departementId]);

    const cochees = useMemo(() => lignes.filter((l) => l.coche), [lignes]);
    const sansAdresse = lignes.filter((l) => !l.origine).length;
    useEffect(() => { if (index >= cochees.length) setIndex(0); }, [cochees.length, index]);

    const edition = (extra = {}) => ({
        etudiants: lignes.map((l) => ({ dossier_id: l.dossier_id, adresse: l.adresse, coche: !!l.coche, prochains: !!l.prochains })),
        cc: cc.map((x) => x.adresse), cci: cci.map((x) => x.adresse), sujet, html: htmlCourant(),
        ...(multi ? { textes: Object.fromEntries(genres.map((g) => [g, texteDe(g)])) } : {}),
        ...extra
    });
    const lireErreur = (e) => ({ message: e.response?.data?.error || "L'opération a échoué", detail: e.response?.data?.detail || null, code: e.response?.data?.code });

    const verifier = () => {
        if (!cochees.length) return `Cochez au moins un ${(brouillon.colonne_ligne || 'étudiant').toLowerCase()}.`;
        const mauvaises = cochees.filter((l) => !ADRESSE_RE.test(String(l.adresse || '').trim()));
        if (mauvaises.length) return `Adresse manquante ou mal formée pour : ${mauvaises.map((l) => `${l.nom} ${l.prenom}`).join(', ')}.`;
        if (multi) {
            const vide = [...new Set(cochees.map((l) => l.genre))].find((g) => !String(texteDe(g).sujet || '').trim());
            if (vide) return `L'objet du « ${brouillon.textes[vide].libelle.toLowerCase()} » est obligatoire.`;
        } else if (!sujet.trim()) return "L'objet est obligatoire.";
        if (brouillon.avec_lien !== false && !/class="[^"]*envoi-lien/.test(htmlCourant())) return "Le lien de la fiche a été effacé : l'envoi est bloqué. Fermez la fenêtre et rouvrez-la pour repartir du modèle.";
        if (brouillon.bloc && !/class="[^"]*envoi-bloc/.test(htmlCourant())) return "La liste des étudiants a été effacée : l'envoi est bloqué. Fermez la fenêtre et rouvrez-la pour repartir du modèle.";
        return null;
    };

    // ── Le suivi d'une tâche de fond ──
    useEffect(() => {
        if (!tache || tache.etat === 'fini') return undefined;
        const minuterie = setInterval(async () => {
            try {
                const r = await (api?.etat || stageAPI.etatPromotion)(departementId, tache.id);
                setTache((t) => ({ ...t, ...r.data }));
            } catch (e) {
                setTache((t) => ({ ...t, etat: 'fini', erreur: lireErreur(e).message }));
            }
        }, 1000);
        return () => clearInterval(minuterie);
    }, [tache, departementId, api]);
    useEffect(() => {
        if (tache?.etat !== 'fini' || tache.mode !== 'essai') return;
        const ok = (tache.resultats || []).filter((x) => x.ok).length;
        setRetour({ ton: ok ? 'ok' : 'ko', texte: <>Essai : <b>{ok} message{ok > 1 ? 's' : ''}</b> envoyé{ok > 1 ? 's' : ''} à <b>{adresseEssai}</b>{tache.redirige_vers ? ` (redirigé vers ${tache.redirige_vers})` : ''} — « [ESSAI] … », bandeau « aurait été envoyé à », lien vers l'aperçu. <b>Rien n'est enregistré dans les dossiers.</b>{tache.erreur ? ` ⚠️ ${tache.erreur}` : ''}</> });
        setTache(null);
    }, [tache, adresseEssai]);

    const lancer = async ({ essai = null, forcer = false, seulement = null } = {}) => {
        const v = verifier();
        if (v && !seulement) { setErreur({ message: v }); return; }
        setOccupe(true); setErreur(null); setGarde(null);
        // Le retour d'un ESSAI précédent ne concerne pas l'envoi réel : il ne doit pas rester sur le bilan.
        if (!essai) setRetour(null);
        try {
            const ed = edition({ forcer_meme_adresse: forcer || !!essai });
            if (seulement) ed.etudiants = ed.etudiants.map((x) => ({ ...x, coche: seulement.includes(x.dossier_id) }));
            const r = api
                ? await api.lancer(departementId, { edition: ed, essai })
                : await stageAPI.lancerPromotion(departementId, { configuration_id: brouillon.configuration_id, edition: ed, essai });
            setTache({ id: r.data.tache_id, total: r.data.total, faits: 0, etat: 'en_cours', mode: essai ? 'essai' : 'envoi', resultats: [] });
            if (!essai) setFait(true);
        } catch (e) {
            const err = lireErreur(e);
            if (err.code === 'MEME_ADRESSE') setGarde(err); else setErreur(err);
        } finally { setOccupe(false); }
    };

    const envoyer = () => {
        // Garde-fou « même adresse », vérifié ici ET par le serveur — sauf quand c'est normal
        // (lot 3.5b-4b : un même maître encadre deux étudiants → deux messages à la même adresse).
        if (brouillon.meme_adresse_permise) { lancer(); return; }
        const vus = {};
        for (const l of cochees) { const k = String(l.adresse || '').toLowerCase(); vus[k] = (vus[k] || 0) + 1; }
        const doublon = Object.entries(vus).find(([, n]) => n > 1);
        if (doublon) { setGarde({ message: `${doublon[1]} messages iraient à la même adresse ${doublon[0]}.` }); return; }
        lancer();
    };

    const essayer = (quoi) => {
        setMenuEssai(false);
        if (!ADRESSE_RE.test(adresseEssai.trim())) { setErreur({ message: "L'adresse d'essai n'est pas valide." }); return; }
        lancer({ essai: { adresse: adresseEssai.trim(), quoi, index } });
    };

    const secours = async (seulement = null) => {
        setOccupe(true); setErreur(null);
        try {
            const r = await stageAPI.mailtoPromotion(departementId, { configuration_id: brouillon.configuration_id, edition: edition(), dossier_ids: seulement });
            const copie = await copier(r.data.adresses);
            setFait(true);
            setRetour({ ton: 'ok', texte: <>Message <b>préparé</b> dans votre messagerie (ancien fonctionnement) : les <b>{r.data.nb} adresses</b> {copie ? 'sont copiées' : 'sont ci-dessous'} — collez-les en <b>Cci</b>. Le lien commun est dans le texte. Le journal note « préparé ».
                <textarea readOnly className="fe-adresses" value={r.data.adresses} onFocus={(e) => e.target.select()} /></> });
            window.location.href = r.data.mailto;
        } catch (e) { setErreur(lireErreur(e)); }
        finally { setOccupe(false); }
    };

    const fermer = () => { if (occupe || (tache && tache.etat !== 'fini')) return; if (fait) onTermine(true); onFermer(); };
    const titre = `${brouillon.titre || 'Envoyer le lien à la promotion'} — ${brouillon.formation || ''}`;
    const sansMailto = brouillon.mailto_secours === false;

    // ── La progression, puis le bilan ──
    if (tache && tache.mode === 'envoi') {
        const res = tache.resultats || [];
        const ok = res.filter((x) => x.ok).length, echecs = res.filter((x) => !x.ok);
        const fini = tache.etat === 'fini';
        return (
            <div className="stg-modale-fond" role="dialog" aria-modal="true" aria-label={titre} onClick={fermer}>
                <div className={'stg-modale fe-fenetre' + taille.classe} style={taille.style} onClick={(e) => e.stopPropagation()}>
                    {taille.poignee}
                    <div className="stg-modale-header fe-titre"><i className="fa-solid fa-paper-plane" /><span>{titre}</span></div>
                    <div className="stg-modale-body fe-corps">
                        <Bandeaux environnement={brouillon.environnement} />
                        <div className="fe-progres"><div style={{ width: `${tache.total ? Math.round(100 * (tache.faits || 0) / tache.total) : 0}%` }} /></div>
                        <div className="fe-progres-texte">{fini ? 'Terminé' : 'Envoi en cours…'} — {tache.faits || 0} / {tache.total}</div>
                        {fini && (
                            <div className={'fe-bilan ' + (echecs.length ? 'fe-bilan-ko' : 'fe-bilan-ok')} style={{ marginBottom: 10 }}>
                                <i className={'fa-solid ' + (echecs.length ? 'fa-triangle-exclamation' : 'fa-circle-check')} />
                                <div>
                                    <b>{ok} envoyé{ok > 1 ? 's' : ''}</b>{echecs.length ? <> · <b>{echecs.length} échec{echecs.length > 1 ? 's' : ''}</b></> : null}
                                    {tache.erreur && <div className="fe-bilan-detail">{tache.erreur}</div>}
                                    {tache.redirige_vers && <div className="fe-bilan-detail"><i className="fa-solid fa-flask" /> Serveur de test : tous ces messages sont partis à {tache.redirige_vers}.</div>}
                                    {tache.recap && <div className="fe-bilan-detail">{tache.recap.envoye
                                        ? <>Récapitulatif envoyé à : {(tache.recap.destinataires || []).join(', ')}.</>
                                        : <>Récapitulatif non envoyé : {tache.recap.erreur || tache.recap.raison}.</>}</div>}
                                    <div className="fe-bilan-detail">{brouillon.note_bilan || "Chaque message est au journal de son dossier, tel qu'envoyé. Les dossiers n'avancent que pour les messages acceptés."}</div>
                                </div>
                            </div>
                        )}
                        <div className="fe-bilan-liste">
                            {res.map((x) => (
                                <div key={x.dossier_id} className="fe-bilan-ligne">
                                    <span className={x.ok ? 'fe-ok' : 'fe-ko'}><i className={'fa-solid ' + (x.ok ? 'fa-check' : 'fa-xmark')} /></span>
                                    <span>{x.nom}</span>
                                    <span className={x.ok ? '' : 'fe-ko'}>{x.ok ? `${x.adresse} · accepté par le serveur` : `${x.adresse || '—'} — ${x.erreur}`}</span>
                                </div>
                            ))}
                        </div>
                        {retour && <div className={'fe-retour ' + (retour.ton === 'ok' ? 'fe-retour-ok' : 'fe-retour-ko')}><i className="fa-solid fa-envelope-open" /><span>{retour.texte}</span></div>}
                        {erreur && <div className="fe-retour fe-retour-ko"><i className="fa-solid fa-circle-xmark" /><span><b>{erreur.message}</b>{erreur.detail ? <> {erreur.detail}</> : null}</span></div>}
                    </div>
                    <div className="stg-modale-pied">
                        {fini && echecs.length > 0 && (
                            <>
                                {!sansMailto && <button type="button" className="btn btn-outline btn-sm" disabled={occupe} onClick={() => secours(echecs.map((x) => x.dossier_id))}>
                                    <i className="fa-solid fa-envelope-open" /> Ouvrir les échecs dans ma messagerie
                                </button>}
                                <button type="button" className="btn btn-sm btn-danger" disabled={occupe}
                                        onClick={() => { setTache(null); lancer({ forcer: true, seulement: echecs.map((x) => x.dossier_id) }); }}>
                                    <i className="fa-solid fa-rotate-right" /> Renvoyer les échecs ({echecs.length})
                                </button>
                            </>
                        )}
                        <button type="button" className="btn btn-green btn-sm" onClick={fermer} disabled={!fini || occupe}>Fermer</button>
                    </div>
                </div>
            </div>
        );
    }

    const etu = cochees[index];
    return (
        <div className="stg-modale-fond" role="dialog" aria-modal="true" aria-label={titre} onClick={fermer}>
            <div className={'stg-modale fe-fenetre' + taille.classe} style={taille.style} onClick={(e) => e.stopPropagation()}>
                {taille.poignee}
                <div className="stg-modale-header fe-titre"><i className="fa-solid fa-paper-plane" /><span>{titre}</span></div>
                <div className="stg-modale-body fe-corps">
                    <Bandeaux environnement={brouillon.environnement} />
                    <div className="fe-apercu-chiffre">
                        <span><b>{cochees.length}</b> message{cochees.length > 1 ? 's' : ''} personnel{cochees.length > 1 ? 's' : ''}</span>
                        {brouillon.apercu_chiffre ? (
                            // Lot C.4a — l'aperçu chiffré de la certification (C32) : à lancer, maîtres trouvés, sans certificateur.
                            <>
                                <span><b>{brouillon.apercu_chiffre.a_lancer}</b> dossier{brouillon.apercu_chiffre.a_lancer > 1 ? 's' : ''} à lancer</span>
                                <span><b>{brouillon.apercu_chiffre.maitres_trouves}</b> maître{brouillon.apercu_chiffre.maitres_trouves > 1 ? 's' : ''} trouvé{brouillon.apercu_chiffre.maitres_trouves > 1 ? 's' : ''}</span>
                                {brouillon.apercu_chiffre.sans_certificateur > 0 && <span className="fe-ko"><i className="fa-solid fa-triangle-exclamation" /> <b>{brouillon.apercu_chiffre.sans_certificateur}</b> sans certificateur (ils reçoivent quand même leur lien)</span>}
                            </>
                        ) : brouillon.resume ? <span>{brouillon.resume}</span> : <span><b>{lignes.length}</b> étudiant{lignes.length > 1 ? 's' : ''} sans réponse</span>}
                        {sansAdresse > 0 && <span className="fe-ko"><i className="fa-solid fa-user-slash" /> <b>{sansAdresse}</b> sans adresse</span>}
                        {brouillon.apercu_chiffre
                            ? brouillon.note_figement && <span className="fe-apercu-note">— {brouillon.note_figement}</span>
                            : !brouillon.resume && <span className="fe-apercu-note">— 1ᵉʳ envoi : la fiche de la campagne sera figée</span>}
                    </div>

                    <div className="fe-ligne"><label>De</label><div className="fe-fixe"><i className="fa-solid fa-lock" /> {brouillon.de?.nom} &lt;{brouillon.de?.adresse}&gt;</div></div>
                    <div className="fe-ligne"><label>Répondre&nbsp;à</label><div className="fe-fixe"><i className="fa-solid fa-lock" /> {brouillon.repondre_a}</div></div>

                    <div className="fe-ligne">
                        <label>À</label>
                        <div className="fe-champ">
                            <div className="fe-aide" style={{ marginTop: 0, marginBottom: 4 }}>Un message <b>personnel</b> par {(brouillon.colonne_ligne || 'étudiant').toLowerCase()} — décocher le retire de cet envoi ; l'adresse se corrige dans sa ligne.</div>
                            {brouillon.groupes ? <ListeGroupes groupes={brouillon.groupes} lignes={lignes} setLignes={setLignes} /> : (
                            <div className="fe-table-cadre">
                                <table className="fe-table">
                                    <thead><tr>
                                        <th><input type="checkbox" aria-label="Tout cocher" checked={cochees.length === lignes.filter((l) => l.adresse).length && cochees.length > 0}
                                                   onChange={(e) => setLignes((ls) => ls.map((l) => ({ ...l, coche: e.target.checked && !!l.adresse })))} /></th>
                                        <th>{brouillon.colonne_ligne || 'Étudiant'}</th><th>{brouillon.colonne_adresse || 'Adresse'}</th>{!brouillon.sans_prochains && <th title="Garder l'adresse corrigée pour les prochains envois de ce dossier">Prochains envois</th>}
                                    </tr></thead>
                                    <tbody>
                                        {lignes.map((l, i) => {
                                            const corrigee = l.adresse !== l.origine && !!l.adresse;
                                            return (
                                                <tr key={l.dossier_id} className={l.coche ? '' : 'fe-decochee'}>
                                                    <td><input type="checkbox" checked={!!l.coche} aria-label={`Envoyer à ${l.nom} ${l.prenom}`}
                                                               onChange={(e) => setLignes((ls) => ls.map((x, k) => (k === i ? { ...x, coche: e.target.checked } : x)))} /></td>
                                                    <td>{l.nom} {l.prenom}{l.relance && <span className="fe-tag">relance</span>}{l.info && <span className="fe-tag">{l.info}</span>}{l.note && <span className="fe-tag fe-tag-alerte">{l.note}</span>}</td>
                                                    <td><input className={'input-sm fe-adr-input' + (corrigee ? ' fe-adr-corrigee' : '') + (l.coche && !ADRESSE_RE.test(String(l.adresse || '').trim()) ? ' fe-refus' : '')}
                                                               value={l.adresse} placeholder={l.origine ? '' : 'aucune adresse — saisissez-en une'}
                                                               onChange={(e) => setLignes((ls) => ls.map((x, k) => (k === i ? { ...x, adresse: e.target.value, coche: x.coche || (!x.origine && !!e.target.value) } : x)))} /></td>
                                                    {!brouillon.sans_prochains && <td style={{ textAlign: 'center' }}><input type="checkbox" disabled={!corrigee} checked={!!l.prochains && corrigee}
                                                               onChange={(e) => setLignes((ls) => ls.map((x, k) => (k === i ? { ...x, prochains: e.target.checked } : x)))} /></td>}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            )}
                        </div>
                    </div>
                    {/* Lot PA.3 — les envois précédents de ce document (Planning annuel), s'il le dit */}
                    {brouillon.deja && (
                        <div className="fe-ligne"><label>Déjà</label>
                            <div className="fe-chips fe-champ">
                                {brouillon.deja.length
                                    ? brouillon.deja.map((x, i) => <Etiquette key={'d' + i} verrou libelle={`envoyé le ${new Date(x.le).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} à ${x.n} personne${x.n > 1 ? 's' : ''}${x.par ? ' · ' + x.par : ''}`} />)
                                    : <span className="fe-aide" style={{ margin: 0 }}>jamais envoyé</span>}
                            </div>
                        </div>
                    )}

                    <div className="fe-ligne">
                        <label>Cc</label>
                        <div className="fe-chips fe-champ">
                            {cc.map((c, i) => (
                                <Etiquette key={'c' + i} libelle={c.nom ? `${c.nom}${c.role ? ' · ' + c.role : ''}` : null} adresse={c.adresse}
                                           onCorriger={(v) => setCc((l) => l.map((y, k) => (k === i ? { ...y, adresse: v } : y)))}
                                           onRetirer={() => setCc((l) => l.filter((_, k) => k !== i))} />
                            ))}
                            <Ajout placeholder="Ajouter en copie…" annuaire={annuaire} onAjouter={(nom, adresse) => setCc((l) => [...l, { nom, adresse }])} />
                        </div>
                    </div>
                    <div className="fe-ligne">
                        <label>Cci</label>
                        <div className="fe-champ">
                            <div className="fe-chips">
                                {brouillon.cci_auto && <Etiquette verrou libelle={`vous-même (${brouillon.cci_auto})`} titre="Vous recevez le récapitulatif. Non retirable." />}
                                {cci.map((c, i) => (
                                    <Etiquette key={'b' + i} libelle={c.nom} adresse={c.adresse}
                                               onCorriger={(v) => setCci((l) => l.map((y, k) => (k === i ? { ...y, adresse: v } : y)))}
                                               onRetirer={() => setCci((l) => l.filter((_, k) => k !== i))} />
                                ))}
                                <Ajout placeholder="Ajouter en copie cachée…" annuaire={annuaire} onAjouter={(nom, adresse) => setCci((l) => [...l, { nom, adresse }])} />
                            </div>
                            <div className="fe-aide"><i className="fa-solid fa-circle-info" /> Envoi collectif : chaque personne en <b>Cc</b> ou <b>Cci</b> (vous compris) reçoit <b>un seul récapitulatif</b> — la liste des destinataires et le 1ᵉʳ message —, pas une copie par étudiant.</div>
                        </div>
                    </div>

                    <div className="fe-onglets" role="tablist">
                        {multi ? genres.map((g) => (
                            // un onglet par texte ; celui d'un genre sans ligne cochée s'efface (comme la maquette)
                            cochees.some((l) => l.genre === g) || g === genreActif ? (
                                <button key={g} type="button" role="tab" aria-selected={onglet === 'rediger' && genreActif === g}
                                        className={'fe-onglet' + (onglet === 'rediger' && genreActif === g ? ' on' : '')}
                                        onClick={() => { changerTexte(g); setOnglet('rediger'); }}>
                                    <i className={'fa-solid ' + (brouillon.textes[g].icone || 'fa-pen-to-square')} /> {brouillon.textes[g].libelle}
                                </button>
                            ) : null
                        )) : (
                        <button type="button" role="tab" aria-selected={onglet === 'rediger'} className={'fe-onglet' + (onglet === 'rediger' ? ' on' : '')}
                                onClick={() => { htmlCourant(); setOnglet('rediger'); }}><i className="fa-solid fa-pen-to-square" /> Rédiger</button>
                        )}
                        <button type="button" role="tab" aria-selected={onglet === 'apercu'} className={'fe-onglet' + (onglet === 'apercu' ? ' on' : '')}
                                onClick={() => { htmlCourant(); setOnglet('apercu'); }}><i className="fa-solid fa-eye" /> Aperçu, message par message</button>
                    </div>

                    {onglet === 'rediger' ? (
                        <>
                            <div className="fe-ligne"><label>Objet</label>
                                <input className="input-sm fe-champ" value={sujet} maxLength={300} onChange={(e) => { retoucheRef.current[cleTexte] = true; setSujet(e.target.value); }} aria-label="Objet" />
                                {onModeles && (
                                    <button type="button" className="btn btn-outline btn-sm fe-modeles" onClick={ouvrirModeles}
                                            title="Ouvre le modèle de courriel de cet envoi (versions, texte d'origine). Enregistré, il remplace le texte de cette fenêtre — on vous demande si vous l'aviez retouché.">
                                        <i className="fa-solid fa-pen" /> Modèles de courriel…
                                    </button>
                                )}
                            </div>
                            {remplacement && (
                                <div className="fe-garde">
                                    <i className="fa-solid fa-triangle-exclamation" />
                                    <div style={{ flex: 1 }}><b>Le modèle de courriel a changé.</b> Vous aviez retouché le texte de cette fenêtre : lequel garder pour cet envoi ?</div>
                                    <button type="button" className="btn btn-outline btn-sm" onClick={() => { poserTexteModele(remplacement.genre, remplacement); setRemplacement(null); }}>Prendre le nouveau modèle</button>
                                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setRemplacement(null)}>Garder mon texte</button>
                                </div>
                            )}
                            <div className="fe-barre" role="toolbar" aria-label="Mise en forme">
                                <button type="button" title="Gras" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }}><b>G</b></button>
                                <button type="button" title="Italique" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }}><i>I</i></button>
                                <button type="button" title="Liste à puces" onMouseDown={(e) => { e.preventDefault(); document.execCommand('insertUnorderedList'); }}><i className="fa-solid fa-list-ul" /></button>
                                {/* Lot 3.5d-0 : un texte LIBRE (entreprises) part de rien — les étiquettes s'insèrent au curseur.
                                    Seulement si le brouillon le demande : la promotion, la certification et les tuteurs ne changent pas. */}
                                {(brouillon.inserer || []).map((x) => (
                                    <button type="button" key={x.var} className="fe-inserer" title={`Insérer l'étiquette « ${x.libelle} » au curseur`}
                                            onMouseDown={(e) => { e.preventDefault(); insererEtiquette(x); }}>
                                        <i className="fa-solid fa-tag" /> {x.libelle}
                                    </button>
                                ))}
                            </div>
                            {/* La clé remonte la zone quand son texte change d'onglet ou est remplacé par le modèle (lot PA.3) */}
                            <div key={`${cleTexte}-${versionTexte}`} className="fe-texte" contentEditable suppressContentEditableWarning ref={poserTexte}
                                 onInput={(e) => { retoucheRef.current[cleTexte] = true; htmlRef.current = e.currentTarget.innerHTML; }} aria-label="Texte du message" />
                            {multi ? <div className="fe-aide"><i className="fa-solid fa-circle-info" /> {brouillon.textes[genreActif].aide}</div>
                            : brouillon.aide_rediger ? <div className="fe-aide"><i className="fa-solid fa-circle-info" /> {brouillon.aide_rediger}</div> : <div className="fe-aide">
                                <i className="fa-solid fa-circle-info" /> Ce texte part à <b>chaque</b> étudiant coché : les étiquettes bleues deviennent <b>son</b> prénom et <b>son</b> nom
                                (dans l'objet : {'{prénom}'} et {'{nom}'}) ; l'étiquette verte est le lien {brouillon.apercu_chiffre ? 'personnel de dépôt' : 'de la fiche'} (effacée → envoi bloqué). Vos modifications valent pour cet envoi seulement.
                            </div>}
                        </>
                    ) : (
                        <div>
                            <div className="fe-nav">
                                <button type="button" className="btn btn-outline btn-sm" disabled={!cochees.length} onClick={() => setIndex((i) => (i - 1 + cochees.length) % cochees.length)}>◀</button>
                                <b>{cochees.length ? index + 1 : 0} / {cochees.length}</b> — tel que le recevra <b>{etu ? (etu.a_libelle ? `${etu.a_libelle} (pour ${`${etu.nom} ${etu.prenom || ""}`.trim()})` : `${etu.nom} ${etu.prenom}`) : '—'}</b>
                                <button type="button" className="btn btn-outline btn-sm" disabled={!cochees.length} onClick={() => setIndex((i) => (i + 1) % cochees.length)}>▶</button>
                            </div>
                            {etu ? (
                                <div className="fe-apercu-msg">
                                    <div className="fe-entetes">
                                        <div><b>À :</b> {etu.a_libelle || `${etu.nom} ${etu.prenom}`} &lt;{etu.adresse || '—'}&gt;</div>
                                        {etu.cc_ligne?.length > 0 && <div><b>Cc :</b> {etu.cc_ligne.map((c) => `${c.nom || ''}${c.role ? ' (' + c.role + ')' : ''} <${c.adresse}>`).join(', ')}</div>}
                                        <div><b>Objet :</b> {Object.entries((multi ? brouillon.textes[etu.genre]?.jetons_objet : brouillon.jetons_objet) || { prenom: '{prénom}', nom: '{nom}' })
                                            .reduce((o, [v, jeton]) => o.split(jeton).join(valeurDe(etu, v)), multi ? texteDe(etu.genre).sujet : sujet)}</div>
                                        <div className="fe-aide" style={{ marginTop: 2 }}>Les copies (Cc : {cc.length ? cc.map((x) => x.adresse).join(', ') : 'aucune'} · Cci : vous) ne figurent pas sur ce message : elles reçoivent le récapitulatif.</div>
                                    </div>
                                    <div className="fe-apercu-corps" dangerouslySetInnerHTML={{ __html: rendreMessage(multi ? texteDe(etu.genre).html : htmlRef.current, etu) }} />
                                </div>
                            ) : <div className="fe-aide">Aucun étudiant coché.</div>}
                        </div>
                    )}

                    {garde && (
                        <div className="fe-garde">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <div style={{ flex: 1 }}><b>{garde.message}</b><br />Est-ce un essai ? Un essai ne crée rien, ne fige rien, et ses liens s'ouvrent en aperçu.</div>
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => { setGarde(null); setMenuEssai(true); }}><i className="fa-solid fa-flask" /> Faire un essai</button>
                            <button type="button" className="btn btn-outline btn-sm fe-forcer" onClick={() => lancer({ forcer: true })}>Envoyer quand même</button>
                        </div>
                    )}
                    {tache && tache.mode === 'essai' && <div className="fe-retour fe-retour-ok"><i className="fa-solid fa-circle-notch stg-tourne" /><span>Essai en cours — {tache.faits || 0} / {tache.total}…</span></div>}
                    {retour && <div className={'fe-retour ' + (retour.ton === 'ok' ? 'fe-retour-ok' : 'fe-retour-ko')}><i className="fa-solid fa-flask" /><span>{retour.texte}</span></div>}
                    {erreur && <div className="fe-retour fe-retour-ko"><i className="fa-solid fa-circle-xmark" /><span><b>{erreur.message}</b>{erreur.detail ? <> {erreur.detail}</> : null}</span></div>}
                </div>

                <div className="stg-modale-pied fe-pied">
                    <span className="fe-journal"><i className="fa-solid fa-book" /> Le journal gardera chaque message tel qu'envoyé.</span>
                    <button type="button" className="btn btn-outline btn-sm" onClick={fermer} disabled={occupe}>{fait ? 'Fermer' : 'Annuler'}</button>
                    {!sansMailto && (
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => secours(null)} disabled={occupe || !cochees.length}
                                title="Ancien fonctionnement : un message, les adresses à coller en Cci, le lien en clair. L'écran dira « préparé ».">
                            <i className="fa-solid fa-envelope-open" /> Ouvrir dans ma messagerie
                        </button>
                    )}
                    <div className="fe-essai">
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => setMenuEssai((o) => !o)} disabled={occupe || !cochees.length}>
                            <i className="fa-solid fa-flask" /> M'envoyer un essai <i className="fa-solid fa-caret-down" />
                        </button>
                        {menuEssai && (
                            <div className="fe-essai-menu">
                                <div className="fe-essai-aide">Tout part à l'adresse d'essai — <b>personne d'autre</b>. Objet « [ESSAI] », bandeau « aurait été envoyé à », lien vers l'aperçu. <b>Rien n'est enregistré</b> dans les dossiers.</div>
                                <label>Adresse d'essai</label>
                                <input className="input-sm" value={adresseEssai} onChange={(e) => setAdresseEssai(e.target.value)} />
                                <button type="button" className="fe-essai-opt" onClick={() => essayer('affiche')}><i className="fa-solid fa-envelope" /> Le message affiché (1){etu ? ` — ${etu.prenom}` : ''}</button>
                                {cochees.length > 3 && <button type="button" className="fe-essai-opt" onClick={() => essayer('trois')}><i className="fa-solid fa-envelopes-bulk" /> Les 3 premiers</button>}
                                <button type="button" className="fe-essai-opt" onClick={() => essayer('tous')}><i className="fa-solid fa-layer-group" /> Tous ({cochees.length})</button>
                            </div>
                        )}
                    </div>
                    <button type="button" className="btn btn-green btn-sm" onClick={envoyer} disabled={occupe || !cochees.length || fait}>
                        {occupe ? <><i className="fa-solid fa-circle-notch stg-tourne" /> Lancement…</> : <><i className="fa-solid fa-paper-plane" /> Envoyer {cochees.length} message{cochees.length > 1 ? 's' : ''}</>}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default StageFenetrePromotion;
